import { useTheme } from 'foundations-theming/utils';
import classnames from 'classnames';
import { COMPLETED, NOT_STARTED } from 'customer-data-objects/engagement/EngagementStatusTypes';
import { getProperty } from 'customer-data-objects/model/ImmutableModel';
import { getId } from 'customer-data-objects/record/ObjectRecordAccessors';
import { STATUS, TITLE } from 'customer-data-objects/task/TaskPropertyNames';
import I18n from 'I18n';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import UIButton from 'UIComponents/button/UIButton';
import UIIconCircle from 'UIComponents/icon/UIIconCircle';
import UIFlex from 'UIComponents/layout/UIFlex';
import { TASK_INTERACTION_TYPES } from '../../constants/table';
import { useOnUpdateTaskStatus } from '../../hooks/tasksCRUD';
import { useFollowUpTasksSetting } from '../../hooks/useFollowUpTasksSetting';
import { useTaskEditPermissions } from '../../hooks/useTaskEditPermissions';
import { useTemporarilyExcludeIds } from '../../hooks/useTemporarilyExcludeIds';
import { useCreateFollowUpTask } from '../../hooks/useCreateFollowUpTask';
import { useTaskDefaults } from '../../hooks/useTaskDefaults';
import { useTaskProperties } from '../../hooks/properties';
import { TABLE_PROPERTIES } from '../../constants/properties';
import FollowUpTasksPopover from 'task-forms-lib/followUp/FollowUpTasksPopover';
import { jsx as _jsx } from "react/jsx-runtime";
const _AnimatedCheckbox = styled(UIIconCircle)(({
  theme
}) => ({
  background: `linear-gradient(to right, ${theme.color['fill-positive-default']} 50%, ${theme.color['fill-tertiary-hover']} 50%)`,
  backgroundSize: '200% 100%',
  backgroundPosition: 'right',
  backgroundOrigin: 'border-box',
  transition: 'all 0.2s ease',
  '&.complete': {
    backgroundPosition: 'left'
  },
  '&.disabled': {
    cursor: 'not-allowed'
  }
}));
const KEEP_TASK_IN_TABLE_TIME = 1000;
const TaskStatusCell = ({
  original: task
}) => {
  const trellisTheme = useTheme();
  const taskId = Number(getId(task));
  const taskTitle = getProperty(task, TITLE);
  const [popoverIsOpen, setPopoverOpen] = useState(false);
  const {
    shouldShowFollowUpTasks
  } = useFollowUpTasksSetting();
  const onUpdateTaskStatus = useOnUpdateTaskStatus();
  const temporarilyExcludeTaskIds = useTemporarilyExcludeIds();
  const handleClosePopover = useCallback(() => {
    setPopoverOpen(false);
    temporarilyExcludeTaskIds([taskId],
    // Follow-up tasks only occur on tasks being completed
    TASK_INTERACTION_TYPES.COMPLETE_TASKS);
  }, [setPopoverOpen, taskId, temporarilyExcludeTaskIds]);
  const taskDefaults = useTaskDefaults();
  const statusProp = getProperty(task, STATUS);
  // TODO Remove null type and change default value of statusTemp (eg to '')
  const [statusTemp, setStatusTemp] = useState(null);
  const status = statusTemp === null ? statusProp : statusTemp;
  const isComplete = status === COMPLETED;

  // the temp status is reset to null so that logic falls back to the statusProp variable.
  // when updating the status via a click, the tempStatus will be used instead of the status
  // prop to start the animation before the API finishes
  useEffect(() => {
    setStatusTemp(null);
  }, [setStatusTemp, statusProp, taskId]);
  const delayedExcludeTask = useCallback(newStatus => {
    setTimeout(() => {
      temporarilyExcludeTaskIds([taskId], newStatus === COMPLETED ? TASK_INTERACTION_TYPES.COMPLETE_TASKS : TASK_INTERACTION_TYPES.UNCOMPLETE_TASKS);
    }, KEEP_TASK_IN_TABLE_TIME);
  }, [temporarilyExcludeTaskIds, taskId]);
  const handleClickStatusButton = useCallback(async () => {
    const newStatus = isComplete ? NOT_STARTED : COMPLETED;
    if (shouldShowFollowUpTasks) {
      setPopoverOpen(newStatus === COMPLETED);
    }
    setStatusTemp(newStatus);
    await onUpdateTaskStatus({
      objectId: taskId,
      newStatus,
      onFailure: () => setStatusTemp(status),
      onSuccess: shouldShowFollowUpTasks ? () => {} : () => delayedExcludeTask(newStatus)
    });
  }, [setPopoverOpen, setStatusTemp, onUpdateTaskStatus, taskId, isComplete, status, delayedExcludeTask, shouldShowFollowUpTasks]);
  const canEditTask = useTaskEditPermissions({
    taskRecord: task
  });
  const createFollowUpTask = useCreateFollowUpTask();
  const isCompleteTaskDisabled = !canEditTask;
  const color = isComplete ? trellisTheme.color['do-not-use-olaf'] : trellisTheme.color['do-not-use-battleship'];
  const borderRadius = isComplete ? 0 : undefined;
  const {
    properties: taskProperties
  } = useTaskProperties(TABLE_PROPERTIES);
  const handleClickCreateFollowUpTask = useCallback(dueDateValue => {
    createFollowUpTask(task, dueDateValue);
    handleClosePopover();
  }, [createFollowUpTask, handleClosePopover, task]);
  const statusButton = /*#__PURE__*/_jsx(UIFlex, {
    justify: "center",
    children: /*#__PURE__*/_jsx(UIButton, {
      title: isCompleteTaskDisabled ? I18n.text('tasks-table.cells.taskStatus.disabledMessage') : undefined,
      "data-test-id": "task-status-cell",
      "data-selenium-test": "task-status-cell",
      "data-task-title": taskTitle,
      use: "unstyled",
      onClick: () => void handleClickStatusButton(),
      disabled: isCompleteTaskDisabled,
      "aria-label": isComplete && isCompleteTaskDisabled ? I18n.text('tasks-table.cells.taskStatus.taskStatusMessage.completedAndDisabled') : isComplete && !isCompleteTaskDisabled ? I18n.text('tasks-table.cells.taskStatus.taskStatusMessage.completed') : !isComplete && isCompleteTaskDisabled ? I18n.text('tasks-table.cells.taskStatus.taskStatusMessage.incompleteAndDisabled') : I18n.text('tasks-table.cells.taskStatus.taskStatusMessage.notCompleted'),
      children: /*#__PURE__*/_jsx(_AnimatedCheckbox, {
        name: "success",
        size: 8,
        borderColor: trellisTheme.color['do-not-use-battleship'],
        className: classnames(isComplete && "complete", isCompleteTaskDisabled && "disabled"),
        color: color
        // @ts-expect-error not defined in UIIconCircle type
        ,
        borderRadius: borderRadius
      })
    })
  });
  return /*#__PURE__*/_jsx(FollowUpTasksPopover, {
    isOpen: popoverIsOpen,
    onClose: handleClosePopover,
    onSubmit: handleClickCreateFollowUpTask,
    setIsOpen: setPopoverOpen,
    taskDefaults: taskDefaults,
    taskProperties: taskProperties,
    taskTitle: taskTitle,
    children: statusButton
  });
};
export default TaskStatusCell;